import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Stack,
    Tooltip,
} from '@mui/material';
import {
    DragIndicator,
    Close,
    Add,
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslate, useNotification } from '@refinedev/core';
import { TEMPLATE_VARIABLES } from './types';

interface TemplateEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    helperText?: string;
    multiline?: boolean;
    rows?: number;
}

// Token types for parsing
type TokenType = 'text' | 'variable';

interface Token {
    id: string;
    type: TokenType;
    value: string;
    displayName?: string;
}

// Parse template string into tokens
const parseTemplate = (template: string): Token[] => {
    const tokens: Token[] = [];
    const regex = /(\$\{[^}]+\})/g;
    let lastIndex = 0;
    let match;
    let tokenId = 0;

    while ((match = regex.exec(template)) !== null) {
        // Add text before the variable
        if (match.index > lastIndex) {
            const textValue = template.slice(lastIndex, match.index);
            if (textValue) {
                tokens.push({
                    id: `token-${tokenId++}`,
                    type: 'text',
                    value: textValue,
                });
            }
        }

        // Add the variable
        const variable = match[1];
        const varInfo = TEMPLATE_VARIABLES.find(v => v.variable === variable);
        tokens.push({
            id: `token-${tokenId++}`,
            type: 'variable',
            value: variable,
            displayName: varInfo?.description,
        });

        lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < template.length) {
        tokens.push({
            id: `token-${tokenId++}`,
            type: 'text',
            value: template.slice(lastIndex),
        });
    }

    return tokens;
};

// Convert tokens back to template string
const tokensToString = (tokens: Token[]): string => {
    return tokens.map(t => t.value).join('');
};

// Sortable Variable Chip component
interface SortableChipProps {
    token: Token;
    onRemove: (id: string) => void;
}

const SortableChip: React.FC<SortableChipProps> = ({ token, onRemove }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: token.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 1,
    };

    if (token.type === 'text') {
        return (
            <Box
                ref={setNodeRef}
                style={style}
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.5,
                    py: 0.25,
                    mx: 0.25,
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    cursor: 'grab',
                    '&:hover': { bgcolor: 'grey.200' },
                }}
                {...attributes}
                {...listeners}
            >
                <DragIndicator sx={{ fontSize: 14, mr: 0.5, color: 'grey.400' }} />
                {token.value}
            </Box>
        );
    }

    return (
        <Tooltip title={token.displayName || token.value} arrow>
            <Chip
                ref={setNodeRef}
                style={style}
                {...attributes}
                {...listeners}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DragIndicator sx={{ fontSize: 14, color: 'inherit', opacity: 0.7 }} />
                        <span>{token.value}</span>
                    </Box>
                }
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => onRemove(token.id)}
                deleteIcon={<Close sx={{ fontSize: 14 }} />}
                sx={{
                    mx: 0.5,
                    my: 0.25,
                    cursor: 'grab',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    '& .MuiChip-label': {
                        px: 1,
                    },
                    '&:active': {
                        cursor: 'grabbing',
                    },
                }}
            />
        </Tooltip>
    );
};

// Main Template Editor Component
export const TemplateEditor: React.FC<TemplateEditorProps> = ({
    value,
    onChange,
    placeholder,
    label,
    helperText,
    multiline = true,
    rows = 4,
}) => {
    const translate = useTranslate();
    const { open } = useNotification();
    const [tokens, setTokens] = useState<Token[]>(() => parseTemplate(value));
    const editorRef = useRef<HTMLDivElement>(null);

    // Update tokens when value changes externally
    React.useEffect(() => {
        const newTokens = parseTemplate(value);
        // Only update if the string representation is different
        if (tokensToString(newTokens) !== tokensToString(tokens)) {
            setTokens(newTokens);
        }
    }, [value]);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setTokens((items) => {
                const oldIndex = items.findIndex((i) => i.id === active.id);
                const newIndex = items.findIndex((i) => i.id === over.id);
                const newItems = arrayMove(items, oldIndex, newIndex);
                // Update parent
                onChange(tokensToString(newItems));
                return newItems;
            });
        }
    }, [onChange]);

    // Remove a token
    const handleRemoveToken = useCallback((tokenId: string) => {
        setTokens((items) => {
            const newItems = items.filter((i) => i.id !== tokenId);
            onChange(tokensToString(newItems));
            return newItems;
        });
    }, [onChange]);

    // Insert a variable at the end
    const handleInsertVariable = useCallback((variable: string) => {
        const varInfo = TEMPLATE_VARIABLES.find(v => v.variable === variable);
        const newToken: Token = {
            id: `token-${Date.now()}`,
            type: 'variable',
            value: variable,
            displayName: varInfo?.description,
        };

        setTokens((items) => {
            const newItems = [...items, newToken];
            onChange(tokensToString(newItems));
            return newItems;
        });

        open?.({
            type: 'success',
            message: `Variable ${variable} ajoutée`,
        });
    }, [onChange, open]);

    // Add text
    const handleAddText = useCallback(() => {
        const text = window.prompt(translate('telesurveilleur.template.addText', 'Entrez le texte à ajouter:'));
        if (text) {
            const newToken: Token = {
                id: `token-${Date.now()}`,
                type: 'text',
                value: text,
            };
            setTokens((items) => {
                const newItems = [...items, newToken];
                onChange(tokensToString(newItems));
                return newItems;
            });
        }
    }, [onChange, translate]);

    const tokenIds = useMemo(() => tokens.map(t => t.id), [tokens]);

    return (
        <Box>
            {label && (
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {label}
                </Typography>
            )}

            {/* Editor area */}
            <Paper
                ref={editorRef}
                variant="outlined"
                sx={{
                    p: 2,
                    minHeight: multiline ? rows * 24 : 48,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    '&:focus-within': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                    },
                }}
            >
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={tokenIds}
                        strategy={horizontalListSortingStrategy}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 0.5,
                                minHeight: 32,
                            }}
                        >
                            {tokens.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic' }}
                                >
                                    {placeholder || translate('telesurveilleur.template.placeholder', 'Cliquez sur les variables ci-dessous pour les ajouter...')}
                                </Typography>
                            )}
                            {tokens.map((token) => (
                                <SortableChip
                                    key={token.id}
                                    token={token}
                                    onRemove={handleRemoveToken}
                                />
                            ))}
                        </Box>
                    </SortableContext>
                </DndContext>

                {/* Add text button */}
                <Box sx={{ mt: 1, borderTop: '1px dashed', borderColor: 'divider', pt: 1 }}>
                    <Tooltip title={translate('telesurveilleur.template.addTextTooltip', 'Ajouter du texte libre')}>
                        <Chip
                            icon={<Add />}
                            label={translate('telesurveilleur.template.addTextBtn', 'Texte')}
                            size="small"
                            variant="outlined"
                            onClick={handleAddText}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Tooltip>
                </Box>
            </Paper>

            {helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {helperText}
                </Typography>
            )}

            {/* Available variables */}
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('telesurveilleur.variables', 'Variables disponibles')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {translate('telesurveilleur.variablesHintInsert', 'Cliquez sur une variable pour l\'insérer dans le template')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {TEMPLATE_VARIABLES.map((v) => (
                        <Tooltip key={v.variable} title={v.description} arrow>
                            <Chip
                                label={v.variable}
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={() => handleInsertVariable(v.variable)}
                                icon={<Add sx={{ fontSize: 16 }} />}
                                sx={{
                                    cursor: 'pointer',
                                    fontFamily: 'monospace',
                                    '&:hover': {
                                        bgcolor: 'rgba(25, 118, 210, 0.12)',
                                    },
                                }}
                            />
                        </Tooltip>
                    ))}
                </Stack>
            </Paper>
        </Box>
    );
};

export default TemplateEditor;
